import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Rocket, ExternalLink, Copy, KeyRound, Trash2, Check, Globe, Terminal, Code2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AssistantAvatar from "@/components/AssistantAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import Playground from "@/admin/Playground";
import BehaviorSettings from "@/admin/BehaviorSettings";
import ConfirmDelete from "@/components/ConfirmDelete";

export default function AssistantPage() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [selected, setSelected] = useState([]);
  const [publications, setPublications] = useState([]);
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [keyName, setKeyName] = useState("");
  const [publishing, setPublishing] = useState(false);

  const loadAssistant = useCallback(async () => {
    const a = await api.getAssistant(aid);
    setAssistant(a);
    setSelected(a.dataset_ids || []);
    api.getDatasets(a.project_id).then(setDatasets).catch(() => {});
    api.getPublications(aid).then(setPublications).catch(() => {});
    api.getApiKeys(aid).then(setKeys).catch(() => {});
  }, [aid]);

  useEffect(() => { loadAssistant(); }, [loadAssistant]);

  const saveDatasets = async () => {
    const a = await api.setDatasets(aid, selected);
    setAssistant(a);
    toast.success("Veri setleri güncellendi");
  };

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const publish = async () => {
    if ((assistant.dataset_ids?.length || 0) === 0) return toast.error("Önce veri seti bağlayın");
    setPublishing(true);
    try {
      await api.publish(aid);
      toast.success("Asistan yayınlandı");
      loadAssistant();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yayınlanamadı");
    } finally { setPublishing(false); }
  };

  const createKey = async () => {
    const k = await api.createApiKey(aid, { name: keyName || "API Anahtarı" });
    setNewKey(k.raw_key);
    setKeyName("");
    api.getApiKeys(aid).then(setKeys);
  };

  const revokeKey = async (id) => {
    await api.revokeApiKey(id);
    toast.success("Anahtar iptal edildi");
    api.getApiKeys(aid).then(setKeys);
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Kopyalandı"); };

  const liveSlug = publications.find((p) => p.status === "live")?.public_slug;
  const hostedUrl = liveSlug ? `${window.location.origin}/a/${liveSlug}` : null;
  const wColor = assistant?.config_draft?.behavior?.widget_color || "#0a0a0a";
  const wPos = assistant?.config_draft?.behavior?.widget_position || "right";
  const embedSnippet = liveSlug
    ? `<script src="${window.location.origin}/embed.js" data-verba-slug="${liveSlug}" data-verba-title="${assistant?.name || "Bilgi Asistanı"}" data-verba-color="${wColor}" data-verba-position="${wPos}" defer></script>`
    : null;

  // Yayınlanmış asistanda taslak, canlı yayından sonra değiştiyse yeniden yayınlanmalı.
  const hasUnpublishedChanges =
    assistant?.status === "published" &&
    !!assistant?.published_at &&
    (assistant?.updated_at || "") > assistant.published_at;

  if (!assistant) return <div className="text-muted-foreground">Yükleniyor...</div>;

  return (
    <div className="fade-up">
      <button
        data-testid="back-project-btn"
        onClick={() => navigate(`/projects/${assistant.project_id}`)}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Proje
      </button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AssistantAvatar
            icon={assistant.config_draft?.behavior?.icon}
            color={assistant.config_draft?.behavior?.widget_color || "#0a0a0a"}
            size={44}
          />
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">{assistant.name}</h1>
            <div className="mt-0.5 flex items-center gap-2">
              <Badge variant="outline" className={assistant.status === "published"
                ? "rounded-full bg-emerald-50 text-emerald-700 border-emerald-200"
                : "rounded-full"}>
                {assistant.status === "published" ? "Yayında" : "Taslak"}
              </Badge>
              <span className="text-xs text-muted-foreground">{assistant.dataset_ids?.length || 0} veri seti</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ConfirmDelete
            testId="delete-assistant-dialog"
            title="Asistanı sil?"
            description={`"${assistant.name}" asistanı, yayınları ve API anahtarları kalıcı olarak silinecek. Bağlı veri setleri silinmez.`}
            onConfirm={async () => {
              await api.deleteAssistant(aid);
              toast.success("Asistan silindi");
              navigate(`/projects/${assistant.project_id}`);
            }}
            trigger={
              <Button data-testid="delete-assistant-btn" variant="outline" className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="mr-1.5 h-4 w-4" /> Sil
              </Button>
            }
          />
          <Button data-testid="publish-btn" onClick={publish} disabled={publishing}
            className={`rounded-full ${hasUnpublishedChanges ? "bg-amber-500 text-white hover:bg-amber-600" : ""}`}>
            <Rocket className="mr-1.5 h-4 w-4" />
            {publishing ? "Yayınlanıyor..." : hasUnpublishedChanges ? "Değişiklikleri Yayınla" : "Yayınla"}
          </Button>
        </div>
      </div>

      {hasUnpublishedChanges && (
        <div
          data-testid="unpublished-changes-banner"
          className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <AlertTriangle className="mt-0.5 h-4.5 w-4.5 shrink-0 text-amber-600" />
          <div>
            <span className="font-semibold">Yayınlanmamış değişiklikler var.</span>{" "}
            Yaptığınız güncellemeler (ikon, davranış, dil, veri setleri) hosted sayfaya ve widget'a yansıması için
            <span className="font-semibold"> “Değişiklikleri Yayınla” </span>butonuna basmanız gerekir.
          </div>
        </div>
      )}

      <Tabs defaultValue="playground" className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger data-testid="tab-playground" value="playground" className="rounded-full">Playground</TabsTrigger>
          <TabsTrigger data-testid="tab-behavior" value="behavior" className="rounded-full">Davranış</TabsTrigger>
          <TabsTrigger data-testid="tab-datasets" value="datasets" className="rounded-full">Veri Setleri</TabsTrigger>
          <TabsTrigger data-testid="tab-publish" value="publish" className="rounded-full">Yayın & API</TabsTrigger>
        </TabsList>

        {/* Playground */}
        <TabsContent value="playground" className="mt-6">
          <div className="soft-card p-5">
            <Playground assistant={assistant} />
          </div>
        </TabsContent>

        {/* Davranış */}
        <TabsContent value="behavior" className="mt-6">
          <BehaviorSettings assistant={assistant} onSaved={(a) => setAssistant(a)} />
        </TabsContent>

        {/* Datasets */}
        <TabsContent value="datasets" className="mt-6">
          <div className="soft-card p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-bold">Bağlı veri setleri</h3>
              <Button data-testid="save-datasets-btn" size="sm" onClick={saveDatasets}>Kaydet</Button>
            </div>
            {datasets.length === 0 ? (
              <p className="text-sm text-muted-foreground">Bu projede henüz veri seti yok.</p>
            ) : (
              <div className="space-y-2">
                {datasets.map((d) => (
                  <div
                    key={d.id}
                    role="button"
                    tabIndex={0}
                    data-testid={`ds-toggle-${d.id}`}
                    onClick={() => toggle(d.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(d.id); } }}
                    className="flex cursor-pointer select-none items-center gap-3 rounded-xl border border-border px-4 py-3 transition-colors hover:bg-secondary/50"
                  >
                    <Checkbox checked={selected.includes(d.id)} className="pointer-events-none" tabIndex={-1} />
                    <div className="flex-1">
                      <div className="font-medium">{d.name}</div>
                      <div className="text-xs text-muted-foreground">{d.stats?.segments || 0} parça · {d.stats?.ready || 0} hazır doküman</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Publish & API */}
        <TabsContent value="publish" className="mt-6 space-y-6">
          {/* Hosted */}
          <div className="soft-card p-5">
            <h3 className="flex items-center gap-2 font-bold"><Globe className="h-4.5 w-4.5" /> Hosted sayfa</h3>
            {hostedUrl ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <code data-testid="hosted-url" className="mono flex-1 truncate rounded-lg bg-secondary px-3 py-2 text-sm">{hostedUrl}</code>
                <Button variant="outline" size="sm" onClick={() => copy(hostedUrl)}><Copy className="mr-1 h-4 w-4" /> Kopyala</Button>
                <Button data-testid="open-hosted-btn" size="sm" onClick={() => window.open(hostedUrl, "_blank")}>
                  <ExternalLink className="mr-1 h-4 w-4" /> Aç
                </Button>
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">Yayınladığınızda herkese açık sayfa linki burada görünür.</p>
            )}
            {publications.length > 0 && (
              <div className="mt-4 space-y-1.5">
                {publications.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                    <span className="mono text-xs">v{p.version} · {p.public_slug}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={p.status === "live" ? "bg-emerald-50 text-emerald-700 border-emerald-200 rounded-full" : "rounded-full"}>
                        {p.status === "live" ? "Canlı" : "Geçmiş"}
                      </Badge>
                      {p.status !== "live" && (
                        <Button data-testid={`rollback-${p.id}`} variant="ghost" size="sm" onClick={async () => { await api.rollback(p.id); loadAssistant(); }}>
                          Geri yükle
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Widget embed */}
          <div className="soft-card p-5">
            <h3 className="flex items-center gap-2 font-bold"><Code2 className="h-4.5 w-4.5" /> Web sitesi widget'ı</h3>
            {embedSnippet ? (
              <>
                <p className="mt-1 text-sm text-muted-foreground">
                  Bu kodu sitenizin <code className="mono text-xs">&lt;/body&gt;</code> etiketinden önce yapıştırın. Sağ altta bir sohbet balonu belirir.
                </p>
                <div className="mt-3 rounded-xl bg-zinc-950 p-4 text-xs text-zinc-100">
                  <pre data-testid="embed-snippet" className="mono whitespace-pre-wrap break-all">{embedSnippet}</pre>
                </div>
                <Button data-testid="copy-embed-btn" variant="outline" size="sm" className="mt-3" onClick={() => copy(embedSnippet)}>
                  <Copy className="mr-1 h-4 w-4" /> Kodu kopyala
                </Button>
              </>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">Yayınladığınızda siteye gömülebilir widget kodu burada görünür.</p>
            )}
          </div>

          {/* API */}
          <div className="soft-card p-5">
            <h3 className="flex items-center gap-2 font-bold"><Terminal className="h-4.5 w-4.5" /> API erişimi</h3>
            <div className="mt-3 flex gap-2">
              <Input data-testid="apikey-name-input" placeholder="Anahtar adı" value={keyName} onChange={(e) => setKeyName(e.target.value)} />
              <Button data-testid="create-apikey-btn" onClick={createKey}><KeyRound className="mr-1 h-4 w-4" /> Oluştur</Button>
            </div>
            <div className="mt-4 space-y-1.5">
              {keys.length === 0 ? (
                <p className="text-sm text-muted-foreground">Henüz API anahtarı yok.</p>
              ) : keys.map((k) => (
                <div key={k.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{k.name}</div>
                    <div className="mono text-xs text-muted-foreground">{k.key_prefix}••••</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={k.status === "active" ? "rounded-full bg-emerald-50 text-emerald-700 border-emerald-200" : "rounded-full"}>
                      {k.status === "active" ? "Aktif" : "İptal"}
                    </Badge>
                    {k.status === "active" && (
                      <Button data-testid={`revoke-${k.id}`} variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => revokeKey(k.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {hostedUrl && (
              <div className="mt-4 rounded-xl bg-zinc-950 p-4 text-xs text-zinc-100">
                <div className="mb-2 text-zinc-400"># API ile sohbet</div>
                <pre className="mono whitespace-pre-wrap break-all">{`curl -X POST ${api.base}/public/chat \\
  -H "Authorization: Bearer <API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"message":"Sorunuz"}'`}</pre>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* New key dialog */}
      <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
        <DialogContent data-testid="new-key-dialog">
          <DialogHeader><DialogTitle>API anahtarınız</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Yeni oluşturulan API anahtarı</DialogDescription>
          <p className="text-sm text-muted-foreground">Bu anahtar yalnızca bir kez gösterilir. Güvenli bir yere kaydedin.</p>
          <div className="flex items-center gap-2 rounded-lg bg-secondary p-3">
            <code className="mono flex-1 truncate text-sm">{newKey}</code>
            <Button size="sm" onClick={() => copy(newKey)}><Copy className="h-4 w-4" /></Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewKey(null)}><Check className="mr-1 h-4 w-4" /> Kaydettim</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
